import { useLayoutEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useProductImageLazyQuery } from '../../graphql';
import {
	componentsState,
	hasNutritionState,
	transItemProductState
} from '../../atoms';
import HeartIconActive from '../../../assets/imgs/heart-icon-active.svg';

import { useAppSelector } from '../../../reduxstore/hooks';
import { Container } from '../Container';
import { Anchor } from '../../../GlobalComponents/Anchor';

export const ProductHeader: React.FC = () => {
	const [oldImage, setOldImage] = useState<string | undefined>();
	const [image, setImage] = useState<string | undefined>();
	const [errored, setErrored] = useState(false);
	const hasNutrition = useRecoilValue(hasNutritionState);

	const [imageQuery, { data, loading }] = useProductImageLazyQuery();

	const components = useRecoilValue(componentsState);
	const product = useRecoilValue(transItemProductState);

	const cupComponentGroupIds = [11, 14];
	const filterComponents = [437, 438, 89, 35];

	const [{ height, opacity }, set] = useState<React.CSSProperties>({
		height: 250,
		opacity: 0
	});

	const favoriteProducts = useAppSelector(
		state => state.accountReducer.profile.favorite_products
	);

	const productId = `${product?.id}`;
	const isFavorite = favoriteProducts
		? favoriteProducts.find(product => `${product.product}` === productId)
		: false;

	useLayoutEffect(() => {
		if (errored) {
			set({ height: 0, opacity: 0 });
		}
	}, [errored, set]);

	useLayoutEffect(() => {
		if (data?.product?.image) {
			setImage(data?.product?.image);
			set({ height: 250, opacity: 1 });
		} else if (data && !loading) {
			set({ height: 0, opacity: 0 });
		}
	}, [data, set, loading]);

	useLayoutEffect(() => {
		if (image) {
			setOldImage(image);
		}
	}, [image]);

	useLayoutEffect(() => {
		if (product?.id) {
			imageQuery({ variables: { productId: String(product.id) } });
			setImage(undefined);
			setErrored(false);
			set({ opacity: 0 });
		}
	}, [product?.id, imageQuery, set]);

	const hasImage = (image ?? oldImage) && !errored;

	return (
		<Container
			styles={{ marginBottom: '0.5rem' }}
			$styles992px={{ marginBottom: 0 }}
		>
			<Container styles={{ position: 'relative' }}>
				<Container
					styles={{
						paddingTop: '3rem',
						position: 'absolute',
						right: 0,
						left: 0,
						zIndex: 1,
						backgroundColor: 'rgba(0,0,0,0)',
						backgroundImage:
							'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))'
					}}
					style={{
						top: `calc(${String(height ?? 0)}px - 3rem)`
					}}
				/>

				<Container
					styles={{
						textAlign: 'center',
						width: '100%',
						overflow: 'hidden',
						transition: 'height 1s ease',
						opacity: opacity
					}}
					style={{
						height: `${height}px`
					}}
				>
					{hasImage && (
						<img
							style={{
								width: '100%',
								height: 'auto',
								maxWidth: '400px'
							}}
							src={image ?? oldImage}
							alt="Product"
							onError={() => setErrored(true)}
						/>
					)}
				</Container>
			</Container>
			<Container
				style={{
					paddingTop: image && !errored ? '1.5rem' : undefined
				}}
			>
				<Container>
					<h1 style={{ color: '#f47920', fontWeight: '700' }}>
						{product?.name ?? 'Product Name'}
						<span style={{ marginLeft: '0.5rem' }}>
							{isFavorite && (
								<img
									src={HeartIconActive}
									alt="favorite"
									style={{ verticalAlign: 'inherit' }}
								/>
							)}
						</span>
					</h1>
					<h6
						style={{
							color: '#f47920',
							textTransform: 'uppercase',
							marginBottom: 0
						}}
					>
						Ingredients
					</h6>
					<small
						style={{
							display: 'block',
							marginBottom: '4px',
							marginTop: 0
						}}
					>
						{components
							.filter(
								component =>
									!component.componentGroups.some(({ id }) =>
										cupComponentGroupIds.includes(id)
									) &&
									!filterComponents.includes(component.id) &&
									!component.suppressNameFromRecipe
							)
							.map(({ name }) => name)
							.sort()
							.join(', ')}
					</small>
					{hasNutrition && (
						<Container
							styles={{ display: 'none' }}
							$styles992px={{ display: 'block' }}
						>
							<Anchor
								styles={{
									fontWeight: 700,
									color: '#f47920',
									display: 'block',
									fontSize: '.875em'
								}}
								target="_blank"
								rel="noreferrer"
								href="https://biggby.com/nutrition"
							>
								Nutrition Information
							</Anchor>
							<Anchor
								styles={{
									fontWeight: 700,
									color: '#f47920',
									display: 'block',
									fontSize: '.875em'
								}}
								target="_blank"
								rel="noreferrer"
								href="https://biggby.com/nutrition"
							>
								Allergen Information
							</Anchor>
						</Container>
					)}
				</Container>
			</Container>
		</Container>
	);
};
